import React, { ForwardRefRenderFunction, forwardRef } from "react";
//#region types
import { FormHandles } from "models/propTypes/genericFormProps";
import { GrossSalaryChangeFormProps } from "models/propTypes/wintSalaryPropTypes";
import { SalaryFormTypes } from "models/formTypes";
import { ChoiceProps, CurrencyInputProps } from "models/propTypes/wintFormPropsTypes";
import { TranslationKeys } from "translations";
import { CurrencyCode } from "models/enums/currency";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import {
  Choice,
  CurrencyInput,
  Description,
  InlineLabel,
  useWintForm,
  VerticalFormField,
  WintForm,
} from "components/common/wintForms";
import { Icon, SimpleTooltip, StaticInfoMessage, StyledButton } from "components/common";
import { Chip, Grid, InputAdornment } from "@mui/material";
import GrossSalaryChangeFloorBanner from "../_partials/grossSalaryChangeFloorBanner";
import GrossSalaryChangeResult from "../_partials/grossSalaryChangeResult";
import { useGrossSalaryChangeContext } from "../context/useGrossSalaryChangeContext";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import clsx from "clsx";
import cssExports from "theme/styles/main.module.scss";
import { WintSalaryEnums } from "models/enums";

const GrossSalaryChangeForm: ForwardRefRenderFunction<FormHandles, GrossSalaryChangeFormProps> = (props, ref) => {
  const {
    defaultValues,
    validationSchema,
    handleSubmit,
    personName,
    recalculateHandler,
    loading,
    availableTaxTypes,
    onFormValidationChange,
  } = props;

  const { companyResult, isTaxTypeSelectionEnabled, minimumTargetGrossSalaryAmount } = useGrossSalaryChangeContext();

  const { wintFormProps, connect, values } = useWintForm<SalaryFormTypes.GrossSalaryChange>({
    formRef: ref,
    defaultValues: defaultValues,
    validationSchema: validationSchema,
    handleSubmit: handleSubmit,
    enableReinitialize: true,
    onFormValidationChange,
  });

  const grossSalaryConnector = connect("grossSalary");
  const taxTypeConnector = connect("taxType");

  const showOneTimeTaxButton = isTaxTypeSelectionEnabled(values.grossSalary);

  const effectiveTaxType =
    showOneTimeTaxButton && values.taxType !== undefined
      ? values.taxType
      : WintSalaryEnums.ChangeGrossSalaryTaxType.TableTax;
  const minFloor =
    effectiveTaxType === WintSalaryEnums.ChangeGrossSalaryTaxType.OneTimeTax
      ? minimumTargetGrossSalaryAmount.oneTimeTaxMinimumAmount
      : minimumTargetGrossSalaryAmount.taxTableMinimumAmount;
  const integerMinFloor = Math.ceil(minFloor);

  const onClickRecalculate = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (values.grossSalary !== undefined && !grossSalaryConnector.hasError) {
      const useOneTimeTax = showOneTimeTaxButton
        ? values.taxType === WintSalaryEnums.ChangeGrossSalaryTaxType.OneTimeTax
        : undefined;
      recalculateHandler(values.grossSalary, useOneTimeTax);
    }
  };

  const onClickFillMin = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    grossSalaryConnector.setValue(integerMinFloor, true);
    const minFloorTaxTypeEnabled = isTaxTypeSelectionEnabled(integerMinFloor);
    const useOneTimeTax = minFloorTaxTypeEnabled
      ? values.taxType === WintSalaryEnums.ChangeGrossSalaryTaxType.OneTimeTax
      : undefined;
    recalculateHandler(integerMinFloor, useOneTimeTax);
  };

  return (
    <WintForm {...wintFormProps}>
      <Description>
        {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.GrossSalaryChangeDescription, "", {
          personName: personName,
        })}
      </Description>
      <Grid item xs={12} sx={{ marginY: "8px" }}>
        <GrossSalaryChangeFloorBanner taxType={values.taxType} showOneTimeTaxButton={showOneTimeTaxButton} />
      </Grid>
      <VerticalFormField<CurrencyInputProps>
        Component={CurrencyInput}
        {...grossSalaryConnector}
        label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.GrossSalaryChangeLabel)}
        decimalPoints={2}
        currency={CurrencyCode.SEK}
        endAdornment={
          !loading && values.grossSalary !== undefined ? (
            <InputAdornment position="end" className={cssExports.inputAdornedEnd}>
              <SimpleTooltip
                text={LocalizationHelperInstance.getFormattedMessage(
                  TranslationKeys.Salary.GrossSalaryChangeRecalculateTooltip
                )}
              >
                <Grid item>
                  <StyledButton
                    size={"xs"}
                    className={clsx(
                      cssExports.iconButton,
                      cssExports.transparent,
                      cssExports.withActiveState,
                      cssExports.active
                    )}
                    onClick={onClickRecalculate}
                  >
                    <Icon icon={IconDefinitionConstants.WRecurring} />
                  </StyledButton>
                </Grid>
              </SimpleTooltip>
            </InputAdornment>
          ) : undefined
        }
        isLoading={loading}
      />
      <Grid
        item
        xs={12}
        className={clsx(cssExports.selectionChipsGroup, cssExports.choiceChipsGroup)}
        sx={{ marginTop: "4px" }}
      >
        <Grid item container className={cssExports.selectionChips}>
          <Grid item className={cssExports.chipContainer}>
            <Chip
              className={cssExports.chip}
              variant="outlined"
              clickable
              onClick={onClickFillMin}
              label={
                integerMinFloor > 0
                  ? LocalizationHelperInstance.getFormattedMessage(
                      TranslationKeys.Salary.GrossSalaryChangeFillMinButtonWithAmount,
                      "",
                      { floor: LocalizationHelperInstance.getFormattedCurrency(integerMinFloor, CurrencyCode.SEK) }
                    )
                  : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.GrossSalaryChangeFillMinButton)
              }
            />
          </Grid>
        </Grid>
      </Grid>
      {showOneTimeTaxButton && (
        <VerticalFormField<ChoiceProps>
          Component={Choice}
          {...taxTypeConnector}
          label={TranslationKeys.Salary.ChangeGrossSalaryTaxTypeLabel}
          showMainLabel
          items={availableTaxTypes}
          descriptionText={TranslationKeys.Salary.ChangeGrossSalaryTaxTypeDescription}
        />
      )}
      <Grid item sx={{ marginTop: "16px" }}>
        <InlineLabel>
          {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.GrossSalaryChangeResultLabel)}
        </InlineLabel>
      </Grid>
      <GrossSalaryChangeResult
        totalCostForCompany={companyResult.totalCostForCompany}
        remainingNetAmount={companyResult.remainingNetAmount}
        alreadyPaidOutNetAmount={companyResult.alreadyPaidOutNetAmount}
        privateTax={companyResult.privateTax}
        socialFees={companyResult.socialFees}
      />
      <Grid item sx={{ marginTop: "8px" }}>
        <StaticInfoMessage>
          {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.GrossSalaryChangeInfoText)}
        </StaticInfoMessage>
      </Grid>
    </WintForm>
  );
};

export default forwardRef(GrossSalaryChangeForm);
